<template>
  <div class="w-full flex justify-center pb-16 px-4 sm:px-8 mt-2">
    <div class="w-full max-w-4xl">
      
      <!-- PAGE HEADER (Decreased font size to fit on one line) -->
      <div class="flex flex-col mb-10 pb-6 border-b border-gray-100 text-left w-full items-start animate-fade-in">
        <h1 class="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
          {{ $t("reviewSkill.rs1") }}
        </h1>
        <p class="text-gray-500 font-medium text-base sm:text-lg">Double check your details before starting your journey.</p>
      </div>

      <!-- 1. MAIN COMPETENCY (HERO CARD) -->
      <div class="animate-fade-in-up">
        <!-- Section Label: Centered and slightly larger -->
        <div class="w-full text-left ml-4 mb-4 mt-2">
          <h3 class="text-sm font-extrabold text-gray-500 uppercase tracking-widest">{{ $t("reviewSkill.rs2") }}</h3>
        </div>
        
        <div class="bg-primary circular-card p-8 sm:p-10 shadow-lg relative overflow-hidden group text-left">
          <!-- Decorative Background Blob -->
          <div class="absolute top-0 right-0 w-40 h-40 bg-white opacity-5 rounded-bl-full pointer-events-none transition-transform duration-700 group-hover:scale-125"></div>

          <div class="relative z-10 flex flex-col sm:flex-row items-start sm:items-center">
            <!-- Icon -->
            <div class="flex-shrink-0 mb-6 sm:mb-0 sm:mr-8">
              <span class="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-2xl shadow-inner">
                <img :src="$store.state.addSkill.competency.icon" alt="icon" class="w-10 h-10 brightness-0 invert transition-transform duration-500 group-hover:scale-110" />
              </span>
            </div>
            
            <!-- Text Content (Strictly Left Aligned) -->
            <div class="flex-grow text-left">
              <h2 class="font-extrabold text-2xl sm:text-3xl text-white mb-3 tracking-tight">
                {{ $store.state.addSkill.competency.comp }}
              </h2>
              <p class="text-white/90 text-base font-medium leading-relaxed m-0">
                {{ $store.state.addSkill.competency.wdd }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- 2 & 3. INITIAL LEVEL & TIMELINE (GRID) -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8 animate-fade-in-up" style="animation-delay: 0.1s;">
        
        <!-- Initial Level Card -->
        <div class="bg-white circular-card p-8 border border-gray-100 shadow-sm flex flex-col relative overflow-hidden hover:shadow-md transition-shadow duration-300 text-left">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-sm font-bold text-gray-400 uppercase tracking-widest">{{ $t("reviewSkill.rs3") }}</h3>
            <span class="p-3 bg-primary/10 text-primary rounded-xl">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            </span>
          </div>
          <!-- Data strictly left aligned -->
          <div class="flex-grow flex flex-col justify-center items-start">
            <div class="flex items-end mb-2">
              <span class="font-extrabold text-5xl text-gray-900 leading-none mr-3">{{ $store.state.addSkill.initialLevel }}</span>
              <span class="font-bold text-xl text-primary mb-1">/ 10</span>
            </div>
            <p class="font-semibold text-gray-500 text-lg m-0">{{ initial_level_text }}</p>
          </div>
        </div>

        <!-- Timeline Card -->
        <div class="bg-white circular-card p-8 border border-gray-100 shadow-sm flex flex-col relative overflow-hidden hover:shadow-md transition-shadow duration-300 text-left">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-sm font-bold text-gray-400 uppercase tracking-widest">{{ $t("reviewSkill.rs4") }}</h3>
            <span class="p-3 bg-primary/10 text-primary rounded-xl">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
            </span>
          </div>
          <!-- Data strictly left aligned -->
          <div class="flex-grow flex flex-col justify-center items-start">
            <h4 class="font-bold text-gray-800 text-xl mb-3">{{ $t("reviewSkill.rs5") }}</h4>
            <div class="flex items-center space-x-3 text-gray-600 font-medium bg-gray-50 p-4 rounded-2xl border border-gray-100">
              <span>{{ formatDate(Date()) }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary flex-shrink-0"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              <span class="text-primary font-bold">{{ formatDate($store.state.addSkill.trainingDuration) }}</span>
            </div>
          </div>
        </div>

      </div>

      <!-- 4. PROGRESS MEASURES -->
      <div class="mt-10 animate-fade-in-up" style="animation-delay: 0.2s;" v-if="storedProgressMeasures && storedProgressMeasures.length > 0">
        <!-- Section Label: Centered and slightly larger -->
        <div class="w-full text-left ml-4 mb-4 mt-6">
          <h3 class="text-sm font-extrabold text-gray-500 uppercase tracking-widest">{{ $t("reviewSkill.rs6") }}</h3>
        </div>
        
        <div class="flex flex-col space-y-4">
          <div
            v-for="(p, indexp) in storedProgressMeasures"
            :key="indexp"
            class="bg-white border border-gray-100 circular-card p-5 sm:p-6 flex items-center shadow-sm hover:shadow-md transition-shadow duration-300 text-left"
          >
            <!-- Icon -->
            <div class="flex-shrink-0 mr-5 relative z-10">
              <span class="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-2xl">
                <img v-if="p.id" :src="require('@/assets/Iconography/Progress measures/PM_' + (parseInt(indexp) + 1) + '.svg')" alt="pm" class="w-8 h-8 opacity-80" style="mix-blend-mode: multiply;" />
              </span>
            </div>
            
            <!-- Text Content (Strictly Left Aligned) -->
            <div class="flex-grow text-left">
              <h4 class="font-extrabold text-lg text-gray-800 mb-1 leading-tight">{{ p.title }}</h4>
              <!-- Corrected class from text-white to text-gray-500 -->
              <p class="text-sm font-medium text-gray-500 m-0">{{ p.pmMap }}</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: "ReviewSkill",
  data() {
    return {
      attribute: "",
      your_initial_level: this.$store.state.addSkill.initialLevel,
      initial_level_text: this.$store.state.addSkill.initialLevelText,
      training_duration: "",
      startDate: "",
      progress_measure: "",
      progress_measure_icon: ""
    };
  },
  computed: {
    custom_progress_measures() {
      return this.$store.state.addSkill.customProgressMeasures;
    },
    storedProgressMeasures() {
      const storedIds = this.$store.state.addSkill.progressMeasures || [];
      const customPMs = this.$store.state.addSkill.customProgressMeasures || [];
      const fullCompetency = this.$store.state.addSkill.competency;
      const PM = [];

      if (fullCompetency && fullCompetency.progressMeasures) {
        for (const pmId of storedIds) {
          const matchedPM = fullCompetency.progressMeasures.find(item => item.id === pmId);
          if (matchedPM) {
            PM.push({
              id: matchedPM.id,
              pm: matchedPM.pm,
              pmMap: matchedPM.pmMap,
              title: matchedPM.title
            });
          }
        }
      }

      for (const custom of customPMs) {
        if (custom && custom.id) {
          PM.push({
            id: custom.id,
            pm: custom.cpm,
            pmMap: custom.cpm,
            title: "Custom Measure" 
          });
        }
      }

      return PM;
    }
  },
  created() {
    this.your_initial_level = this.$store.state.addSkill.initialLevel;
    if (this.your_initial_level > 0 && this.your_initial_level <= 2) {
      this.initial_level_text = this.$t("rateYourLevel.ryl4")
    } else if (this.your_initial_level > 2 && this.your_initial_level <= 5) {
      this.initial_level_text = this.$t("rateYourLevel.ryl5")
    } else if (this.your_initial_level > 5 && this.your_initial_level <= 7) {
      this.initial_level_text = this.$t("rateYourLevel.ryl6")
    } else if(this.your_initial_level > 7 && this.your_initial_level < 10){
      this.initial_level_text = this.$t("rateYourLevel.ryl7")
    } else if(this.your_initial_level === 10){
      this.initial_level_text = this.$t("rateYourLevel.ryl8")
    }
  },
  methods: {
    formatDate(createdAt) {
      if (!createdAt) return "";
      let date = new Date(createdAt);
      let year = date.getFullYear();
      let month = String(date.getMonth() + 1).padStart(2, '0');
      let day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    },
  }
};
</script>

<style scoped>
/* Base Animations */
.animate-fade-in {
  animation: fadeIn 0.4s ease-out forwards;
  opacity: 0;
}
.animate-fade-in-up {
  animation: fadeInDown 0.5s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  opacity: 0;
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes fadeInDown {
  0% { opacity: 0; transform: translateY(15px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* 
  CIRCULAR CARDS FIX
  Explicitly forcing border-radius using !important to override any other styles 
  that might be stripping Tailwind's rounded classes. 
*/
.circular-card {
  border-radius: 1.75rem !important; 
}

/* Image formatting */
.brightness-0 { filter: brightness(0); }
.invert { filter: brightness(0) invert(1); }
</style>